// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import { ArrowPosition } from "@synd/App/Components/XngCarousel";
import AngleDown from "@synd/XDL/Icons/AngleDown";
import AngleUp from "@synd/XDL/Icons/AngleUp";
import PaginationLeftArrow from "@synd/XDL/Icons/PaginationLeftArrow";
import PaginationRightArrow from "@synd/XDL/Icons/PaginationRightArrow";

import { color, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export const arrowIconsByDirection = {
    top: {
        src: AngleUp,
        size: 14,
    },
    bottom: {
        src: AngleDown,
        size: 14,
    },
    left: {
        src: PaginationLeftArrow,
        size: 18,
    },
    right: {
        src: PaginationRightArrow,
        size: 18,
    },
};

export const getArrowStyles = ({
    arrowPosition,
    showOnMobile = false,
    hideOnDisabled,
}: {
    arrowPosition: ArrowPosition;
    showOnMobile: boolean;
    hideOnDisabled?: boolean;
}) => {
    const mobileArrowStyles = css`
        &.arrow-on-desktop {
            display: ${showOnMobile ? "flex" : "none"};
        }
    `;
    return {
        size: 16,
        css: css`
            &.show-arrows-outside {
                position: relative;
            }
            &:not(.show-arrows-outside) {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
            }

            background: ${color("common.ui1", 0.9)};
            border: 1px solid ${color("common.ui9")};
            width: 36px;
            height: 36px;
            flex-shrink: 0;
            border-radius: 100%;
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            z-index: 430;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;

            & > ${IconWrapper} {
                color: ${color("common.ui9")};
                transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;

                &,
                & > svg {
                    width: auto;
                }
            }

            &:not(.swiper-button-disabled):hover {
                border-color: ${color("common.ui10")};

                & > ${IconWrapper} {
                    color: ${color("common.ui10")};
                }
            }

            &:focus {
                box-shadow: none;
            }

            ${arrowPosition === "left" &&
            css`
                &:not(.show-arrows-outside) {
                    left: 16px;
                }
            `};

            ${arrowPosition === "right" &&
            css`
                &:not(.show-arrows-outside) {
                    right: 16px;
                }
            `};

            ${arrowPosition === "top" &&
            css`
                &:not(.show-arrows-outside) {
                    top: 8px;
                }
            `};

            ${arrowPosition === "bottom" &&
            css`
                &:not(.show-arrows-outside) {
                    top: calc(100% - 8px) !important;
                }
            `};

            ${(arrowPosition === "top" || arrowPosition === "bottom") &&
            css`
                width: 18px;
                height: 18px;
                &:not(.show-arrows-outside) {
                    position: absolute;
                    inset: 0;
                    transform: none;
                    margin: 0;
                }
                & > ${IconWrapper} {
                    &,
                    & > svg {
                        width: 10px;
                    }
                }
            `};

            &.swiper-button-disabled {
                opacity: 0.33;
                cursor: not-allowed;
                ${hideOnDisabled &&
                css`
                    display: none;
                `}
            }

            ${xScreen("lte", "md")} {
                ${mobileArrowStyles}
            }
        `,
    };
};
